import dayjs, { Dayjs } from 'dayjs';
import { DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, FormikErrors, useField } from 'formik';
import { FormattedMessage } from 'react-intl';
import { ReactElement, ReactNode, useEffect, useState } from 'react';

import { PoolDatePicker, PoolDatePickerNewProps } from './pool-date-picker';

export interface FormikPoolDatePickerProps extends Omit<PoolDatePickerNewProps, 'name' | 'onChange'> {
    name: string; // redefined as required
    setTimeToMidday?: boolean;
    onChange?: (
        value: Dayjs | null,
        helper: FieldHelperProps<Dayjs | null>,
        field: FieldInputProps<Dayjs | null>,
        meta: FieldMetaProps<Dayjs | null>
    ) => void;
}

export const FormikPoolDatePicker = (props: FormikPoolDatePickerProps): ReactElement => {
    const { onChange, setTimeToMidday = true, ...pickerProps } = props;
    const [field, meta, helper] = useField<Dayjs | null>(props.name);
    const hasFormikError = meta.touched && !!meta.error;

    const formatDate = (date: Dayjs | null): Dayjs | null =>
        date ? (setTimeToMidday ? dayjs(date).hour(12) : dayjs(date)) : null;
    const [currentValue, setCurrentValue] = useState<Dayjs | null>(formatDate(field.value));

    useEffect(() => {
        if (field.value !== currentValue) {
            updateInternalValue(field.value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value]);

    const updateInternalValue = (value: Dayjs | null): void => {
        setCurrentValue(formatDate(value));
    };

    const propagateChange = (value: Dayjs | null): void => {
        const isNullOrValid = value === null || (value && value.isValid());
        // By the day, and with no date as one absence: the field holds the day
        // at noon, the saved value another hour of it or undefined, and merely
        // leaving the field marked it as unsaved.
        const isEmpty = null === field.value || undefined === field.value;
        const hasChanged = value === null ? !isEmpty : isEmpty || !value.isSame(field.value, 'day');

        if (isNullOrValid && hasChanged) {
            helper.setValue(value);
            onChange?.(value, helper, field, meta);
        }
    };

    return (
        <PoolDatePicker
            {...pickerProps}
            helperText={hasFormikError ? <FormattedMessage id={meta.error} /> : undefined}
            name={field.name}
            error={hasFormikError}
            value={currentValue}
            onChange={updateInternalValue}
            onAccept={value => {
                updateInternalValue(value);
                propagateChange(value);
            }}
            onBlur={() => propagateChange(currentValue)}
        />
    );
};
